/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { WINDOW } from '../helpers/window.injectToken'

@Injectable({
  providedIn: 'root',
})
export class PidxScrollToService {
  private window = inject<Window>(WINDOW)

  scrollTo(element: Element): void {
    if (element) {
      element.scrollIntoView({ behavior: this.getScrollBehavior() })
    }
  }

  scrollToCoordinates(top: number, left = 0) {
    this.window.scrollTo({ top, left, behavior: this.getScrollBehavior() })
  }

  scrollToElement(element: Element | null, offset = 0) {
    if (!element) {
      return
    }
    const bcl = element.getBoundingClientRect()
    const top = this.window.scrollY + bcl.top - offset
    const behavior = this.getScrollBehavior()
    this.window.scrollTo({ top, left: 0, behavior })
  }

  /**
   * Waits for an element matching the given tag name to appear in the DOM.
   * Uses MutationObserver instead of fixed timeouts for reliability on slower devices (e.g. iOS Safari).
   */
  waitForElement(tagName: string, timeoutMs = 5000): Promise<Element | null> {
    const existing = this.window.document.getElementsByTagName(tagName)[0]
    if (existing) {
      return Promise.resolve(existing)
    }

    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        const el = this.window.document.getElementsByTagName(tagName)[0]
        if (el) {
          observer.disconnect()
          resolve(el)
        }
      })

      observer.observe(this.window.document.body, { childList: true, subtree: true })

      this.window.setTimeout(() => {
        observer.disconnect()
        const el = this.window.document.getElementsByTagName(tagName)[0]
        resolve(el || null)
      }, timeoutMs)
    })
  }

  /**
   * Waits for an element's layout to stabilize (height stops changing).
   * Useful after accordion open animations.
   */
  waitForLayoutStable(element: Element, maxWaitMs = 2000): Promise<void> {
    return new Promise((resolve) => {
      let lastHeight = element.getBoundingClientRect().height
      let stableCount = 0
      const startTime = performance.now()

      const check = () => {
        const currentHeight = element.getBoundingClientRect().height
        if (currentHeight === lastHeight) {
          stableCount++
        } else {
          stableCount = 0
          lastHeight = currentHeight
        }

        if (stableCount >= 3 || performance.now() - startTime > maxWaitMs) {
          resolve()
          return
        }

        requestAnimationFrame(check)
      }

      requestAnimationFrame(check)
    })
  }

  isIOS(): boolean {
    const ua = this.window.navigator?.userAgent || ''
    return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in this.window.document)
  }

  private getScrollBehavior(): ScrollBehavior {
    // iOS Safari has issues with smooth scrolling (momentum interference, rubber-banding)
    return this.isIOS() ? 'instant' : 'smooth'
  }
}
